import { FC, MutableRefObject, useContext } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { Loading } from "@analyticsMD/quil";
import APIContext from "../../contexts/APIContext";
import NotificationToast from "../notification-toast";
import ErrorPage from "../error-page";
import EmbededAppContainer from "../embedded-container";
import TestSection from "../sections/test-section";
import { ReactNode } from "react-markdown";
import QCardSections from "../qcard-sections";
import { Box } from "@mui/material";
import { Sizes } from "../../types/loadingSizes";
import theme from "../../theme";
import ProductionBanner from "../production-banner";

interface QCardContentProps {
  showRoundingMode?: boolean;
  useSidebarView?: boolean; // For drawer, force narrow layout
  updateRef?: MutableRefObject<Record<string, any>>;
  closeButtonCmpt?: ReactNode;
}

const TOAST_ERROR_MESSAGE =
  "We encountered an issue while updating patient data. Please try again";

const sxStyles = {
  background: {
    backgroundColor: "transparent",
  },
  base: {
    minWidth: "20rem",
    maxHeight: "56rem",
  },
};

const QCardContent: FC<QCardContentProps> = ({
  useSidebarView = false,
  updateRef,
  closeButtonCmpt,
}) => {
  const {
    patientQuery,
    configurationQuery,
    dispositionsQuery,
    patientDispositionsQuery,
    intelligenceTextQuery,
    medicallyReadyStatusQuery,
    eddQuery,
    patientConsiderationsQuery,
    patientMilestonesQuery,
    patientInsightsQuery,
    userPreferencesQuery,
    showErrorToast,
    setShowErrorToast,
  } = useContext(APIContext);

  const allQueries = [
    patientQuery,
    dispositionsQuery,
    patientDispositionsQuery,
    configurationQuery,
    intelligenceTextQuery,
    medicallyReadyStatusQuery,
    eddQuery,
    patientConsiderationsQuery,
    patientMilestonesQuery,
    patientInsightsQuery,
    userPreferencesQuery,
  ];

  const allQueriesSuccessful = allQueries.every((query) => query.isSuccess);
  const anyQueryErrors = allQueries.some((query) => query.isError);
  const queryErrors = () => {
    return new Error(
      allQueries
        .filter((query) => query.isError)
        .map((query) => query.error)
        .join(", ")
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={sxStyles.background}>
        <EmbededAppContainer>
          <Box sx={sxStyles.base}>
            {configurationQuery.isSuccess &&
            configurationQuery.data[0].test_mode_enabled ? (
              <TestSection />
            ) : null}
            {userPreferencesQuery.isSuccess && (
              <ProductionBanner
                username={userPreferencesQuery.data[0].emr_user_id}
              />
            )}
            {!allQueriesSuccessful && !anyQueryErrors && (
              <Box sx={{ marginTop: "25%" }} data-testid="page">
                <Loading message={"Loading..."} logoSize={Sizes.Medium} />
              </Box>
            )}
            {allQueriesSuccessful && !anyQueryErrors && (
              <>
                <QCardSections
                  useSidebarView={useSidebarView}
                  updateRef={updateRef}
                  closeButtonCmpt={closeButtonCmpt}
                />
                <NotificationToast
                  message={TOAST_ERROR_MESSAGE}
                  showToast={showErrorToast}
                  isError={true}
                  onClose={() => setShowErrorToast(false)}
                />
              </>
            )}
            {anyQueryErrors && (
              <ErrorPage
                error={queryErrors()}
                includeHeader={true}
                closeButtonCmpt={closeButtonCmpt}
              />
            )}
          </Box>
        </EmbededAppContainer>
      </Box>
    </ThemeProvider>
  );
};

export default QCardContent;
